import React from 'react';
import { motion } from 'framer-motion';

// RIB Boat (sporty inflatable)
export const RIBBoat = ({ className = '', size = 'md' }) => {
  const sizes = {
    sm: { width: 100, height: 60 },
    md: { width: 160, height: 96 },
    lg: { width: 220, height: 132 }
  };
  const { width, height } = sizes[size];

  return (
    <motion.svg 
      width={width} 
      height={height} 
      viewBox="0 0 160 96" 
      fill="none"
      className={className}
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    >
      {/* Inflatable tubes (gray rubber) */}
      <ellipse cx="80" cy="70" rx="75" ry="18" fill="#6B7280" />
      <ellipse cx="80" cy="70" rx="75" ry="18" fill="url(#ribGradient)" />
      
      {/* Inner hull */}
      <path
        d="M25 65 L35 35 L125 35 L135 65 Z"
        fill="#1F2937"
      />
      
      {/* Console/windshield */}
      <rect x="55" y="30" width="50" height="25" rx="4" fill="#374151" />
      <rect x="60" y="32" width="40" height="15" rx="2" fill="#7DD3FC" opacity="0.6" />
      
      {/* Steering wheel */}
      <circle cx="80" cy="50" r="8" stroke="#9CA3AF" strokeWidth="2" fill="none" />
      
      {/* Engine */}
      <rect x="120" y="45" width="25" height="30" rx="3" fill="#111827" />
      <rect x="130" y="75" width="8" height="15" fill="#4B5563" />
      
      {/* Speed stripes */}
      <path d="M30 55 L50 55" stroke="#EF4444" strokeWidth="3" strokeLinecap="round" />
      <path d="M30 60 L45 60" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
      
      {/* Water spray effect */}
      <ellipse cx="80" cy="88" rx="70" ry="8" fill="rgba(56, 189, 248, 0.3)" />
      
      <defs>
        <linearGradient id="ribGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#9CA3AF" />
          <stop offset="100%" stopColor="#4B5563" />
        </linearGradient>
      </defs>
    </motion.svg>
  );
};

// Exclusive Yacht (comfort cruiser)
export const ExclusiveBoat = ({ className = '', size = 'md' }) => {
  const sizes = {
    sm: { width: 120, height: 80 },
    md: { width: 180, height: 120 },
    lg: { width: 240, height: 160 }
  };
  const { width, height } = sizes[size];

  return (
    <motion.svg 
      width={width} 
      height={height} 
      viewBox="0 0 180 120" 
      fill="none"
      className={className}
      animate={{ y: [0, -5, 0], rotate: [-0.5, 0.5, -0.5] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      {/* Hull */}
      <path
        d="M10 85 Q20 105, 90 105 Q160 105, 170 85 L150 60 L30 60 Z"
        fill="white"
        stroke="#E5E7EB"
        strokeWidth="2"
      />
      
      {/* Deck */}
      <rect x="25" y="55" width="130" height="10" rx="2" fill="#F3F4F6" />
      
      {/* Cabin */}
      <rect x="40" y="25" width="80" height="35" rx="6" fill="white" stroke="#E5E7EB" strokeWidth="2" />
      
      {/* Windows */}
      <rect x="48" y="32" width="25" height="15" rx="3" fill="#7DD3FC" opacity="0.7" />
      <rect x="80" y="32" width="25" height="15" rx="3" fill="#7DD3FC" opacity="0.7" />
      
      {/* Sundeck (front) */}
      <ellipse cx="145" cy="55" rx="20" ry="8" fill="#FBBF24" opacity="0.3" />
      <rect x="130" y="48" width="25" height="3" rx="1" fill="#D97706" />
      
      {/* Railing */}
      <path d="M30 55 L30 45 M50 55 L50 45 M130 55 L130 45 M150 55 L150 45" stroke="#9CA3AF" strokeWidth="1.5" />
      <path d="M28 45 L152 45" stroke="#9CA3AF" strokeWidth="1.5" />
      
      {/* Blue accent stripe */}
      <path d="M25 75 L155 75" stroke="#0EA5E9" strokeWidth="4" strokeLinecap="round" />
      
      {/* Anchor symbol */}
      <circle cx="35" cy="75" r="5" fill="#0EA5E9" />
      <text x="32" y="78" fontSize="8" fill="white">⚓</text>
      
      {/* Water */}
      <ellipse cx="90" cy="110" rx="80" ry="10" fill="rgba(56, 189, 248, 0.3)" />
      
    </motion.svg>
  );
};

// Generic boat icon for selection
export const BoatIcon = ({ type, selected = false, className = '' }) => {
  const fillColor = selected ? '#0EA5E9' : '#94A3B8';
  
  if (type === 'rib') {
    return (
      <svg width="48" height="32" viewBox="0 0 48 32" fill="none" className={className}>
        <ellipse cx="24" cy="22" rx="22" ry="6" fill={fillColor} />
        <path d="M8 20 L12 10 L36 10 L40 20 Z" fill={fillColor} />
        <rect x="18" y="8" width="12" height="8" rx="2" fill={selected ? '#7DD3FC' : '#CBD5E1'} />
      </svg>
    );
  }
  
  return (
    <svg width="48" height="32" viewBox="0 0 48 32" fill="none" className={className}>
      <path d="M4 24 Q8 30, 24 30 Q40 30, 44 24 L38 16 L10 16 Z" fill={fillColor} />
      <rect x="12" y="6" width="24" height="12" rx="3" fill={fillColor} />
      <rect x="16" y="9" width="7" height="5" rx="1" fill={selected ? '#7DD3FC' : '#CBD5E1'} />
      <rect x="26" y="9" width="7" height="5" rx="1" fill={selected ? '#7DD3FC' : '#CBD5E1'} />
    </svg>
  );
};

// Animated boat for unlock game
export const GameBoat = ({ className = '' }) => {
  return (
    <motion.div className={className}>
      <svg width="100" height="70" viewBox="0 0 100 70" fill="none">
        {/* Simple cartoon boat */}
        <path
          d="M10 50 Q15 65, 50 65 Q85 65, 90 50 L75 35 L25 35 Z"
          fill="#FACC15"
          stroke="#D97706"
          strokeWidth="2"
        />
        <rect x="30" y="20" width="40" height="20" rx="4" fill="#FACC15" stroke="#D97706" strokeWidth="2" />
        <rect x="38" y="25" width="10" height="10" rx="2" fill="#7DD3FC" />
        <rect x="52" y="25" width="10" height="10" rx="2" fill="#7DD3FC" />
        {/* Flag */}
        <line x1="50" y1="20" x2="50" y2="5" stroke="#D97706" strokeWidth="2" />
        <path d="M50 5 L65 10 L50 15 Z" fill="#EC4899" />
      </svg>
    </motion.div>
  );
};

export default { RIBBoat, ExclusiveBoat, BoatIcon, GameBoat };
